import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { ChevronLeft, X } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import { useAuth } from "@/contexts/AuthContext";
import { homePathFor } from "@/lib/roles";

/**
 * The page shell the legal pages share — Disclaimer & Terms, and the Privacy Policy.
 *
 * It exists for the exit control, not the chrome. That control is the part of these pages with a
 * bug history (see below), and it would have been copied into the privacy page verbatim: two
 * copies of subtle navigation logic, one of which gets fixed. Everything else here — the dark
 * palette, the max-width column, the gold kicker — is just the two pages agreeing on how a legal
 * page looks, which is also worth having in one place.
 *
 * **Dark, like the rest of the site.** The disclaimer was once the only page built on the cream
 * `#FEFDF8` document palette — the colour used for a rendered stamp-paper sheet, not for a page —
 * so following the footer link dropped the reader onto what looked like a different website.
 *
 * `updated` is the revision date the page prints. The two pages source it differently and
 * deliberately: the terms carry `TERMS_VERSION`, which is recorded against every order, while the
 * privacy policy's date is its own (see pages/Privacy.jsx).
 */
export default function LegalShell({ kicker = "Legal", title, updated, backTestId, children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const home = homePathFor(user?.role);
  // One control, three outcomes, because these pages are reached three ways and each needs a
  // different exit. Whatever happens, the reader must end up somewhere — the bug this replaces
  // was a hard `to="/"` that dropped a signed-in admin on the marketing page, which has no
  // signed-in state and offers only a customer Sign In button: indistinguishable from having
  // been signed out, with the session in fact untouched.
  //
  //   new tab (the footer link, the payment checkbox)  -> Close it
  //   followed in-tab (the landing page's prose link)  -> back to what they were reading
  //   opened cold (a pasted link, Google's consent screen) -> their own portal
  //
  // `history.length === 1` is the test for "this tab holds nothing else", which is also exactly
  // when the browser permits window.close() — a tab with one history entry is script-closable.
  // Read once on mount, because it changes as soon as anything navigates.
  const [closable] = useState(() => typeof window !== "undefined" && window.history.length === 1);
  const dismiss = () => {
    if (closable) {
      window.close();
      // Some browsers refuse to close a tab they did not script-open. If we are still here a beat
      // later, leave by the ordinary route rather than sitting on a button that does nothing.
      window.setTimeout(() => { if (!window.closed) navigate(home, { replace: true }); }, 150);
      return;
    }
    // React Router's own position in the session history: 0 means this is the first entry.
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(home);
  };
  return (
    <div className="min-h-screen bg-[#080C16] text-[#F9F8F6] flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#080C16]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={home} className="font-serif-display text-xl flex items-center gap-2">
            <BrandMark size="w-5 h-5" /> DharmaTN
          </Link>
          <button type="button" onClick={dismiss} data-testid={backTestId}
                  className="inline-flex items-center gap-1 text-sm text-[#A0AABF] hover:text-white transition-colors">
            {closable
              ? <><X className="w-4 h-4" strokeWidth={1.5}/> Close</>
              : <><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back</>}
          </button>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16 flex-1">
        <div className="text-xs tracking-[0.24em] uppercase text-[#CBA96C] mb-3">{kicker}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-3">{title}</h1>
        <div className="text-xs text-[#6B7280] mb-10">Last updated {updated}</div>

        <div className="space-y-8 text-[#A0AABF] leading-relaxed text-sm md:text-base">
          {children}
        </div>
      </div>
      <SiteFooter/>
    </div>
  );
}

// Headings are bold and white against the grey body text, so the eye can find a clause without
// reading the paragraphs — these pages are long, and they are consulted far more often than read.
export function Part({ title, children }) {
  return (
    <section className="space-y-6">
      <h2 className="font-serif-display text-2xl md:text-3xl font-bold text-white pt-4">{title}</h2>
      {children}
    </section>
  );
}

export function Clause({ title, children }) {
  return (
    <section className="space-y-3">
      <h3 className="font-bold text-white text-base">{title}</h3>
      {children}
    </section>
  );
}

export function SubClause({ title, children }) {
  return (
    <div className="space-y-2">
      <h4 className="font-bold text-[#DDE3EE] text-sm">{title}</h4>
      {children}
    </div>
  );
}
